import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { isBrowser } from "./utils";
import { asosBreakpoints } from "./asosMediaQueries";

const toMqlEntries = (querySet) =>
  Object.entries(querySet).map(([label, query]) => [
    label,
    window.matchMedia(query),
  ]);

const toMatcherMap = (mqlArray) =>
  mqlArray.reduce(
    (initState, [label, mql]) => ({
      ...initState,
      [label]: mql.matches,
    }),
    {}
  );

const buildRegisterHandlers = (mqlArray) => (setMatchers) => {
  const disposers = mqlArray.map(([label, mql]) => {
    const changeHandler = buildChangeHandler(label, setMatchers);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", changeHandler);
      return () => mql.removeEventListener("change", changeHandler);
    } else {
      // Deprecated methods still needed when running on ios Safari pre version 14
      mql.addListener(changeHandler);
      return () => mql.removeListener(changeHandler);
    }
  });
  return () => disposers.forEach((dispose) => dispose());
};

const stateNeedsUpdating = (currentMatcher, event) => {
  return (
    typeof currentMatcher !== undefined && currentMatcher !== event.matches
  );
};

const buildChangeHandler = (label, setMatchers) => (event) => {
  setMatchers((currentState) => {
    const currentMatcher = currentState[label];
    if (stateNeedsUpdating(currentMatcher, event)) {
      return { ...currentState, [label]: event.matches };
    }
    return currentState;
  });
};

const processQuerySet = (querySet, defaultMatch) => {
  if (isBrowser()) {
    const mqlArray = toMqlEntries(querySet);
    return {
      initialState: toMatcherMap(mqlArray),
      registerHandlers: buildRegisterHandlers(mqlArray),
    };
  } else {
    return {
      initialState: { [defaultMatch]: true },
    };
  }
};

export const MediaContext = React.createContext();

export const MediaContextProvider = ({
  querySet = asosBreakpoints,
  defaultMatch,
  children,
}) => {
  const { initialState, registerHandlers } = processQuerySet(
    querySet,
    defaultMatch
  );
  const [matchers, setMatchers] = useState(initialState);

  useEffect(() => {
    if (typeof registerHandlers === "function") {
      return registerHandlers(setMatchers);
    }
  }, [querySet, registerHandlers]);

  return (
    <MediaContext.Provider value={matchers}>{children}</MediaContext.Provider>
  );
};

MediaContextProvider.displayName = "MediaContextProvider";

MediaContextProvider.propTypes = {
  querySet: PropTypes.object,
  defaultMatch: PropTypes.string,
  children: PropTypes.node,
};
